import { Controller } from 'stimulus'
import { DocumentSaveSession } from '../tiptap/document_save'
import { buildDeliveryUI } from '../tiptap/document_delivery_ui'
import { buildDocumentExport } from '../tiptap/document_export'

const settingLabels = { theme: 'Appearance', font_size: 'Text size', line_spacing: 'Line spacing', font_family: 'Typeface', high_contrast: 'Text contrast', editor_format: 'Document format' }
const saveReasons = {
  connection: 'We couldn’t reach the server. Check your connection and try again.',
  timeout: 'The save timed out. Please try again.',
  session: 'Sign in again in another tab, then try again.',
  permission: 'You don’t have permission to save these changes.',
  size: 'These changes are too large for the server to accept.',
  rejected: 'The server rejected the save. Keep this tab open; your session or these changes may need attention.',
  busy: 'Too many save requests. Wait a moment, then try again.',
  server: 'The server couldn’t save these changes. Please try again.',
  unknown: 'We couldn’t confirm these changes were saved. Please try again.',
}
function saveFailure(reason = 'unknown') {
  const error = new Error(saveReasons[reason])
  error.saveMessage = error.message
  return error
}

function visibleControl(element) {
  if (!element?.isConnected || element.hidden || element.disabled || element.getAttribute('aria-disabled') === 'true') return false
  if (element.getClientRects && element.getClientRects().length === 0) return false
  const style = window.getComputedStyle?.(element)
  return !style || (style.visibility !== 'hidden' && style.display !== 'none')
}

export default class extends Controller {
  static values = { autosave: Boolean, wordUrl: String, pdfUrl: String, markdownUrl: String }

  connect() {
    this.form = this.element.querySelector('#publishForm')
    const header = this.element.querySelector('.editor-header-right')
    if (!this.form || !header) return
    this.sections = new Map()
    this.settings = new Map()
    this.disposed = false
    this.saveTimeCarrier = this.element.querySelector('[data-document-save-time]')
    this.ui = buildDeliveryUI(header, {
      // Server-rendered last save time for this document; refreshed from the
      // save responses below instead of being guessed in the browser.
      savedAt: this.saveTimeCarrier?.dataset.documentSaveTime,
      onSavedAt: value => {
        if (value && this.saveTimeCarrier) this.saveTimeCarrier.dataset.documentSaveTime = value
      },
      autosave: () => this.autosaveOn(),
      sectionAutosave: () => Boolean(this.element.querySelector('.sections-list')),
      fallbackOpener: () => {
        const compact = window.matchMedia?.('(max-width: 1399px)').matches
        const selectors = compact
          ? ['.editor-document-save', '.editor-document-toggle', '.editor-document-tools', '.editor-focus-indicator', '.editor-header-left .nav-toggle']
          : ['[data-save-summary]', '.editor-document-tools', '.editor-document-toggle', '.editor-focus-indicator', '.editor-header-left .nav-toggle']
        return selectors.map(selector => this.element.querySelector(selector)).find(visibleControl)
      },
    })
    this.session = new DocumentSaveSession(state => {
      if (this.disposed) return
      this.ui.render(state)
      this.exporter?.changed()
      document.dispatchEvent(new CustomEvent('document-save:state', { detail: state }))
    })
    this.initialStatus = this.form.querySelector('#post_status')?.value
    this.session.register('document', {
      label: 'Title and document', snapshot: () => this.formSnapshot(),
      save: async snapshot => {
        const body = new FormData()
        JSON.parse(snapshot).forEach(([key,value]) => body.append(key,value))
        const response = await this.request(this.form.action, { method: (this.form.method || 'post').toUpperCase(), body })
        if (response.success !== true) throw saveFailure()
        this.redirectUrl = response.redirect_url
        if (response.updated_at) this.ui.setSavedAt(response.updated_at)
      },
    })
    this.form._documentDelivery = this
    this.exporter = buildDocumentExport(this.ui, {
      session: this.session, sync: () => this.sync(), wordUrl: this.wordUrlValue, pdfUrl: this.pdfUrlValue,
      markdownUrl: this.markdownUrlValue,
      title: () => this.form.querySelector('[name="post[title]"]')?.value,
    })
    this.listen(this.form, 'input', event => {
      const section = event.target.closest('[data-controller~="section-tiptap"]')
      this.changed(section ? `section:${section.dataset.sectionTiptapSectionIdValue}` : 'document')
    })
    this.listen(this.form, 'change', () => this.changed('document'))
    this.listen(document, 'click', event => {
      const button = event.target.closest('button, input[type="submit"]')
      if (button?.form === this.form && button.name === 'status') {
        const field = this.form.querySelector('#post_status')
        if (field) { field.value = button.value; this.changed('document') }
      }
    })
    this.listen(document, 'turbo:before-visit', event => {
      this.sync()
      if (!['error', 'saving', 'unsaved'].includes(this.session.state().status)) return
      event.preventDefault()
      if (this.navigating) return
      this.navigating = true
      this.saveAll().then(saved => {
        if (this.disposed) return
        this.navigating = false
        if (saved) window.Turbo.visit(event.detail.url)
        else this.ui.open('save')
      })
    })
    this.listen(this.form, 'ajax:before', event => {
      event.preventDefault();clearTimeout(window.failedSave)
      this.saveAll().then(saved => {
        if (saved && this.initialStatus !== this.form.querySelector('#post_status')?.value && this.redirectUrl) window.location.assign(this.redirectUrl)
      })
    })
    this.listen(this.element, 'section:registered', event => this.registerSection(event.detail.controller))
    this.listen(this.element, 'section:unregistered', event => {
      const section = this.sections.get(event.detail.sectionId)
      if (section?._deleting) { this.session.remove(`section:${event.detail.sectionId}`);this.sections.delete(event.detail.sectionId) }
      this.refreshLoading()
    })
    this.listen(document, 'document-save:changed', event => this.changed(event.detail?.key || 'document'))
    this.listen(document, 'theme:persisted', event => {
      const theme = event.detail?.theme
      if (this.settings.has('theme') && ['light', 'dark'].includes(theme)) {
        this.session.save(this.setSetting('theme', theme))
      }
    })
    this.listen(document, 'tiptap:section-loaded', () => this.refreshLoading())
    this.listen(this.element, 'click', event => {
      const link = event.target.closest('[data-document-export]')
      const saveDetails = event.target.closest('[data-document-save-details]')
      const origin = (link || saveDetails)?.closest('#editorDocumentMenu') && window.matchMedia('(max-width: 1399px)').matches
        ? this.element.querySelector('.editor-document-toggle') : link || saveDetails
      if (link) { event.preventDefault();this.openExport(link.dataset.documentExport, origin) }
      if (saveDetails) { event.preventDefault();this.sync();this.ui.open('save', origin) }
    })
    this.listen(this.ui.trigger, 'click', () => { this.sync();this.ui.open('save', this.ui.trigger) })
    this.listen(this.ui.dialog, 'click', event => {
      if (event.target.closest('[data-save-all]')) this.saveAll()
      if (event.target.closest('[data-retry-loading]')) this.retryLoading()
    })
    this.listen(window, 'beforeunload', event => {
      this.sync()
      if (['error','saving','unsaved'].includes(this.session.state().status)) { event.preventDefault();event.returnValue='' }
    })
    window.sectionEditorControllers?.forEach(section => this.registerSection(section))
    this.refreshLoading()
  }

  listen(element, event, callback) {
    this.listeners = this.listeners || []
    element.addEventListener(event, callback)
    this.listeners.push(() => element.removeEventListener(event, callback))
  }

  formSnapshot() {
    return JSON.stringify(Array.from(new FormData(this.form).entries()).filter(([name,value]) => !['authenticity_token','utf8'].includes(name) && typeof value === 'string'))
  }

  sync() {
    if (this.disposed) return
    this.sections.forEach(section => section.syncToField())
    const body = this.form.querySelector('#post_body')
    if (body && window.tiptapEditor && !window.tiptapEditor.isDestroyed) body.value = window.tiptapEditor.getHTML().replace(/ data-chnode="[^"]*"/g, '')
    this.refreshLoading()
    this.session.notify()
  }

  changed(key) {
    this.session.changed(key)
    if (this.autosaveValue && key === 'document') {
      clearTimeout(this.saveTimer)
      this.saveTimer = setTimeout(() => this.saveDocument(), 1500)
    }
  }

  // The server-rendered value is the existing document scheduler policy. It is
  // intentionally stable for this editor session: changing a draft to a
  // pending publication state must not silently change persistence behavior.
  autosaveOn() {
    return Boolean(this.autosaveValue)
  }

  registerSection(section) {
    const key = `section:${section.sectionIdValue}`
    this.sections.set(section.sectionIdValue, section)
    section.syncToField()
    this.session.register(key, {
      label: section.element.dataset.sectionLabel || `Section ${section.positionValue + 1}`,
      snapshot: () => section.bodyFieldTarget.value,
      save: async (body, { keepalive = false } = {}) => {
        const response = await this.request(`/posts/${section.postIdValue}/sections/${section.sectionIdValue}`, { method: 'PATCH', json: { post_section: { body } }, keepalive })
        if (response.status !== 'success') throw saveFailure()
        section._lastSavedBody = body
        if (response.updated_at) this.ui.setSavedAt(response.updated_at)
      },
    })
    this.refreshLoading()
  }

  refreshLoading() {
    const frames = Array.from(this.element.querySelectorAll('.sections-list > turbo-frame'))
    const loaded = frames.filter(frame => frame.querySelector('.ProseMirror')).length
    this.session.setLoading(loaded, frames.length)
  }

  retryLoading() {
    this.element.querySelectorAll('.sections-list > turbo-frame').forEach(frame => {
      if (frame.querySelector('.ProseMirror') || frame.hasAttribute('busy')) return
      const src = frame.dataset.sectionSrc || frame.getAttribute('src')
      if (!src) return
      if (frame.getAttribute('src') && typeof frame.reload === 'function') frame.reload()
      else frame.setAttribute('src', src)
    })
  }

  async trackStructure(label, work) {
    const finish = this.session.beginOperation(label)
    try { return await work() }
    finally { this.refreshLoading(); finish() }
  }

  saveSection(section, keepalive = false) {
    if (!this.session.resources.has(`section:${section.sectionIdValue}`)) this.registerSection(section)
    section.syncToField()
    return this.session.save(`section:${section.sectionIdValue}`, { keepalive })
  }

  saveDocument() { this.sync();return this.session.save('document') }
  saveAll() { clearTimeout(this.saveTimer);this.sync();return this.session.saveAll() }
  openExport(format = 'word', opener) { this.sync();this.exporter.open(format, opener) }

  setSetting(setting, value) {
    const key = `setting:${setting}`
    if (!this.settings.has(setting)) {
      this.settings.set(setting, undefined)
      this.session.register(key, {
        label: settingLabels[setting] || 'Editor preference', snapshot: () => this.settings.get(setting),
        save: async snapshot => {
          const format = setting === 'editor_format'
          const result = await this.request(format ? '/post_options/update_editor_format' : '/ai_options/update_editor_settings', {
            method: 'POST', json: format ? {editor_format:snapshot} : {setting,value:snapshot},
          })
          if (result.success !== true) throw saveFailure()
        },
      })
    }
    if (this.settings.get(setting) !== value) { this.settings.set(setting,value);this.session.changed(key) }
    if (setting === 'editor_format') document.dispatchEvent(new CustomEvent('document-save:format', { detail: { value } }))
    return key
  }

  async request(url, { json, ...options }) {
    const abort = new AbortController()
    const timer = setTimeout(() => abort.abort(), 30000)
    try {
      const headers = { Accept:'application/json', 'X-CSRF-Token':document.querySelector('meta[name="csrf-token"]')?.content || '' }
      if (json) { headers['Content-Type']='application/json';options.body=JSON.stringify(json) }
      const response = await fetch(url, { ...options, headers, credentials:'same-origin', signal:abort.signal }).catch(error => {
        throw saveFailure(error.name === 'AbortError' ? 'timeout' : error instanceof TypeError ? 'connection' : 'unknown')
      })
      if (response.redirected) throw saveFailure('session')
      if (!response.ok) {
        const reason = { 401: 'session', 403: 'permission', 408: 'timeout', 413: 'size', 422: 'rejected', 429: 'busy' }[response.status]
        throw saveFailure(reason || (response.status >= 500 ? 'server' : 'unknown'))
      }
      return await response.json().catch(() => { throw saveFailure() })
    } finally { clearTimeout(timer) }
  }

  disconnect() {
    this.disposed = true
    clearTimeout(this.saveTimer)
    this.listeners?.forEach(remove => remove())
    this.exporter?.destroy()
    this.ui?.destroy()
    if (this.form?._documentDelivery === this) delete this.form._documentDelivery
  }
}
